import { useEffect, useState, type ReactNode } from 'react';
import { amountText, type Asset } from './assets.js';
import { aboutOf, localCurrency, localOf, setLocalCurrency, usdRates } from './localMoney.js';

/**
 * The chosen currency, for both trading screens: the browser's and the one
 * inside Telegram.
 *
 * One hook holds the choice and the day's rates, and hands out the two ways a
 * figure is shown. `read` is for a figure a person only reads: their own
 * currency first, the dollars beside it. `about` is for a box whose figure
 * stays in USDC because it is typed or signed: a small "about R 74" under it.
 * The rates are the browser's own once-a-day fetch; the box never calls out.
 */
export interface LocalMoney {
  /** The currency chosen in Account or Settings, or the region's; null means dollars alone. */
  currency: string | null;
  /** Today's dollar rates, null until they arrive or when there are none. */
  rates: Record<string, number> | null;
  /** The currency figures are really read in: null when today's rates lack it. */
  readIn: string | null;
  /** A dollar figure as it is read: the chosen currency, the dollars beside it. */
  read: (usd: number | null, dollars: string, compact?: boolean) => ReactNode;
  /** The same for an amount in an asset's smallest units; only USDC is money. */
  readAmount: (asset: Asset, units: bigint) => ReactNode;
  /** "about R 74", for under a box that stays in dollars; null when there is none. */
  about: (usd: number | null) => string | null;
  /** What the picker in Account or Settings calls; "off" turns the line off. */
  choose: (code: string) => void;
}

export function useLocalMoney(): LocalMoney {
  const [currency, setCurrency] = useState<string | null>(() => localCurrency());
  const [rates, setRates] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    if (currency === null) return;
    let live = true;
    // A choice the cached day's rates lack fetches them again, so a new pick shows at once.
    void usdRates(undefined, undefined, currency).then((r) => {
      if (live) setRates(r);
    });
    return () => {
      live = false;
    };
  }, [currency]);

  const read = (usd: number | null, dollars: string, compact = false): ReactNode => {
    const local = usd === null ? null : localOf(usd, currency, rates, { compact });
    return local === null ? (
      dollars
    ) : (
      <>
        {local}
        <span className="wt-usd">{dollars}</span>
      </>
    );
  };

  return {
    currency,
    rates,
    readIn: localOf(1, currency, rates) === null ? null : currency,
    read,
    readAmount: (asset, units) =>
      read(asset === 'USDC' ? Number(units) / 1e6 : null, amountText(asset, units)),
    about: (usd) => aboutOf(usd, currency, rates),
    choose: (code) => {
      setLocalCurrency(code);
      setCurrency(code === 'off' ? null : code);
    },
  };
}
